import React, { useState, useEffect } from 'react';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  RefreshCw,
  Zap,
  ChevronRight,
  Database,
  Play,
  DollarSign,
  Ship,
  FileText,
  Info,
  X,
  Anchor,
  Receipt
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadBytesResumable, uploadString } from 'firebase/storage';
import { getFirestore, getDocs, doc, setDoc } from 'firebase/firestore';
import { biTableStatusCollection } from '../services/biRecords';
import {
  TARGET_FILE_TYPES,
  buildTargetStoragePath,
  buildTargetUploadPayload,
  cleanTargetCsv,
  requireTargetTenantId,
  targetUploadMetadataPath
} from './TargetBalanceSheetWorkflow';

export default function ActualCostWorkflow({ tenantId } = {}) {
  // Step states
  const [currentStep, setCurrentStep] = useState(1);

  // Shipping Data upload state (Step 1)
  const [shippingFile, setShippingFile] = useState(null);
  const [shippingUploading, setShippingUploading] = useState(false);
  const [shippingProgress, setShippingProgress] = useState(0);
  const [shippingResult, setShippingResult] = useState(null);
  const [shippingError, setShippingError] = useState(null);

  // Duty Data upload state (Step 2)
  const [dutyFile, setDutyFile] = useState(null);
  const [dutyUploading, setDutyUploading] = useState(false);
  const [dutyProgress, setDutyProgress] = useState(0);
  const [dutyResult, setDutyResult] = useState(null);
  const [dutyError, setDutyError] = useState(null);

  // Freight Data upload state (Step 3)
  const [freightFile, setFreightFile] = useState(null);
  const [freightUploading, setFreightUploading] = useState(false);
  const [freightProgress, setFreightProgress] = useState(0);
  const [freightResult, setFreightResult] = useState(null);
  const [freightError, setFreightError] = useState(null);

  // Booking Data upload state (Step 3b)
  const [bookingFile, setBookingFile] = useState(null);
  const [bookingUploading, setBookingUploading] = useState(false);
  const [bookingProgress, setBookingProgress] = useState(0);
  const [bookingResult, setBookingResult] = useState(null);
  const [bookingError, setBookingError] = useState(null);

  // Matson Carton Dims upload state (same destination as Data > Target > Upload Files)
  const [matsonDimsFile, setMatsonDimsFile] = useState(null);
  const [matsonDimsUploading, setMatsonDimsUploading] = useState(false);
  const [matsonDimsProgress, setMatsonDimsProgress] = useState(0);
  const [matsonDimsResult, setMatsonDimsResult] = useState(null);
  const [matsonDimsError, setMatsonDimsError] = useState(null);

  // Transform state (Step 4)
  const [transforming, setTransforming] = useState(false);
  const [transformResult, setTransformResult] = useState(null);
  const [transformError, setTransformError] = useState(null);

  // Table info popup state
  const [tableStatuses, setTableStatuses] = useState({});
  const [activeInfoPopup, setActiveInfoPopup] = useState(null);

  const db = getFirestore();

  // Load table statuses (CHA-616: per tenant, so reload when the tenant is known or changes)
  useEffect(() => {
    // CHA-616 — status is tenant data: drop the previous tenant's statuses immediately, and let only a
    // response for the STILL-CURRENT tenant commit (a slow tenant-A read must not land after tenant B).
    setTableStatuses({});
    if (!tenantId) return undefined;
    let current = true;
    loadTableStatuses(tenantId, () => current);
    return () => { current = false; };
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTableStatuses = async (forTenantId, isCurrent) => {
    try {
      // CHA-616 — the tenant's own status records (CHA-87 E2a), not the shared root collection.
      const statusRef = biTableStatusCollection(db, forTenantId);
      const snapshot = await getDocs(statusRef);
      if (!isCurrent()) return; // the tenant changed while this read was in flight
      const statuses = {};
      snapshot.forEach(doc => {
        statuses[doc.id] = { id: doc.id, ...doc.data() };
      });
      setTableStatuses(statuses);
    } catch (error) {
      console.error('Error loading table statuses:', error);
    }
  };

  // Handle file selections
  const handleShippingFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setShippingFile(file);
      setShippingResult(null);
      setShippingError(null);
    }
  };

  const handleDutyFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setDutyFile(file);
      setDutyResult(null);
      setDutyError(null);
    }
  };

  const handleFreightFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFreightFile(file);
      setFreightResult(null);
      setFreightError(null);
    }
  };

  const handleBookingFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setBookingFile(file);
      setBookingResult(null);
      setBookingError(null);
    }
  };

  const handleMatsonDimsFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setMatsonDimsFile(file);
      setMatsonDimsResult(null);
      setMatsonDimsError(null);
    }
  };

  // Upload Shipping Data
  const uploadShippingData = async () => {
    if (!shippingFile) return;

    setShippingUploading(true);
    setShippingProgress(0);
    setShippingError(null);

    try {
      const storage = getStorage();
      const storagePath = `inmocean-raw-data/actual-cost-workflow/shipping_data.xlsx`;
      const storageRef = ref(storage, storagePath);

      await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, shippingFile);

        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 50);
            setShippingProgress(progress);
          },
          (error) => reject(error),
          () => resolve()
        );
      });

      setShippingProgress(55);
      const functions = getFunctions();
      const processBIUpload = httpsCallable(functions, 'processBIUpload', { timeout: 600000 });

      setShippingProgress(60);
      const result = await processBIUpload({
        bucketName: 'actualizer2.firebasestorage.app',
        filePath: storagePath,
        tableName: 'ac_shipping_data',
        datasetName: 'inmocean_raw',
        tenantId
      });

      setShippingProgress(100);
      setShippingResult({
        success: true,
        filename: shippingFile.name,
        rowCount: result.data?.rowsInserted || result.data?.rowCount || 0
      });
      setShippingUploading(false);
      setCurrentStep(2);
    } catch (err) {
      setShippingError(err.message);
      setShippingUploading(false);
    }
  };

  // Upload Duty Data
  const uploadDutyData = async () => {
    if (!dutyFile) return;

    setDutyUploading(true);
    setDutyProgress(0);
    setDutyError(null);

    try {
      const storage = getStorage();
      const storagePath = `inmocean-raw-data/actual-cost-workflow/duty_charges_data.xlsx`;
      const storageRef = ref(storage, storagePath);

      await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, dutyFile);

        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 50);
            setDutyProgress(progress);
          },
          (error) => reject(error),
          () => resolve()
        );
      });

      setDutyProgress(55);
      const functions = getFunctions();
      const processBIUpload = httpsCallable(functions, 'processBIUpload', { timeout: 600000 });

      setDutyProgress(60);
      const result = await processBIUpload({
        bucketName: 'actualizer2.firebasestorage.app',
        filePath: storagePath,
        tableName: 'ac_duty_charges_data',
        datasetName: 'inmocean_raw',
        tenantId
      });

      setDutyProgress(100);
      setDutyResult({
        success: true,
        filename: dutyFile.name,
        rowCount: result.data?.rowsInserted || result.data?.rowCount || 0
      });
      setDutyUploading(false);
      setCurrentStep(3);
    } catch (err) {
      setDutyError(err.message);
      setDutyUploading(false);
    }
  };

  // Upload Freight Data
  const uploadFreightData = async () => {
    if (!freightFile) return;

    setFreightUploading(true);
    setFreightProgress(0);
    setFreightError(null);

    try {
      const storage = getStorage();
      const storagePath = `inmocean-raw-data/actual-cost-workflow/freight_cost_data.xlsx`;
      const storageRef = ref(storage, storagePath);

      await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, freightFile);

        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 50);
            setFreightProgress(progress);
          },
          (error) => reject(error),
          () => resolve()
        );
      });

      setFreightProgress(55);
      const functions = getFunctions();
      const processBIUpload = httpsCallable(functions, 'processBIUpload', { timeout: 600000 });

      setFreightProgress(60);
      const result = await processBIUpload({
        bucketName: 'actualizer2.firebasestorage.app',
        filePath: storagePath,
        tableName: 'ac_freight_cost_data',
        datasetName: 'inmocean_raw',
        tenantId
      });

      setFreightProgress(100);
      setFreightResult({
        success: true,
        filename: freightFile.name,
        rowCount: result.data?.rowsInserted || result.data?.rowCount || 0
      });
      setFreightUploading(false);
      setCurrentStep(4);
    } catch (err) {
      setFreightError(err.message);
      setFreightUploading(false);
    }
  };

  // Upload Booking Data
  const uploadBookingData = async () => {
    if (!bookingFile) return;

    setBookingUploading(true);
    setBookingProgress(0);
    setBookingError(null);

    try {
      const storage = getStorage();
      const storagePath = `inmocean-raw-data/actual-cost-workflow/booking_data.xlsx`;
      const storageRef = ref(storage, storagePath);

      await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, bookingFile);

        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 50);
            setBookingProgress(progress);
          },
          (error) => reject(error),
          () => resolve()
        );
      });

      setBookingProgress(55);
      const functions = getFunctions();
      const processBIUpload = httpsCallable(functions, 'processBIUpload', { timeout: 600000 });

      setBookingProgress(60);
      const result = await processBIUpload({
        bucketName: 'actualizer2.firebasestorage.app',
        filePath: storagePath,
        tableName: 'ac_booking_data',
        datasetName: 'inmocean_raw',
        tenantId
      });

      setBookingProgress(100);
      setBookingResult({
        success: true,
        filename: bookingFile.name,
        rowCount: result.data?.rowsInserted || result.data?.rowCount || 0
      });
      setBookingUploading(false);
    } catch (err) {
      setBookingError(err.message);
      setBookingUploading(false);
    }
  };

  const uploadMatsonCartonDims = async () => {
    if (!matsonDimsFile) return;

    setMatsonDimsUploading(true);
    setMatsonDimsProgress(0);
    setMatsonDimsError(null);

    try {
      const activeTenantId = requireTargetTenantId(tenantId);
      const matsonCard = TARGET_FILE_TYPES.find((ft) => ft.key === 'matson_carton_dims');
      if (!matsonCard) throw new Error('Matson Carton Dims upload type is not configured.');

      const storage = getStorage();
      const isCSV = matsonDimsFile.name.toLowerCase().endsWith('.csv');
      const storagePath = buildTargetStoragePath(matsonCard, matsonDimsFile.name, activeTenantId);
      const storageRef = ref(storage, storagePath);
      let metadata = {};

      if (isCSV) {
        setMatsonDimsProgress(10);
        const rawText = await matsonDimsFile.text();
        const cleaned = cleanTargetCsv(rawText);
        metadata = cleaned.metadata;
        setMatsonDimsProgress(20);
        await uploadString(storageRef, cleaned.cleanedCsv, 'raw', { contentType: 'text/csv' });
      } else {
        setMatsonDimsProgress(10);
        await new Promise((resolve, reject) => {
          const uploadTask = uploadBytesResumable(storageRef, matsonDimsFile);
          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = 10 + Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 40);
              setMatsonDimsProgress(progress);
            },
            (error) => reject(error),
            () => resolve()
          );
        });
      }

      if (Object.keys(metadata).length > 0) {
        await setDoc(doc(db, ...targetUploadMetadataPath(matsonCard, activeTenantId)), {
          ...metadata,
          tenantId: activeTenantId,
          tableName: matsonCard.table,
          originalFilename: matsonDimsFile.name,
          uploadedAt: new Date().toISOString()
        });
      }

      setMatsonDimsProgress(60);
      const functions = getFunctions();
      const processBIUpload = httpsCallable(functions, 'processBIUpload', { timeout: 600000 });
      const result = await processBIUpload(buildTargetUploadPayload(matsonCard, storagePath, activeTenantId));

      setMatsonDimsProgress(100);
      setMatsonDimsResult({
        success: true,
        filename: matsonDimsFile.name,
        rowCount: result.data?.rowsInserted || result.data?.rowCount || 0,
        metadata
      });
    } catch (err) {
      setMatsonDimsError(err.message);
    } finally {
      setMatsonDimsUploading(false);
    }
  };

  // Run Transform
  const runTransform = async () => {
    setTransforming(true);
    setTransformResult(null);
    setTransformError(null);

    try {
      const functions = getFunctions();
      const runActualCostTransform = httpsCallable(functions, 'runActualCostTransform', { timeout: 570000 });

      const result = await runActualCostTransform();
      setTransformResult(result.data);
      setCurrentStep(5);
    } catch (err) {
      setTransformError(err.message);
    } finally {
      setTransforming(false);
    }
  };

  // Data Source upload state
  const [dsFile, setDsFile] = useState(null);
  const [dsUploading, setDsUploading] = useState(false);
  const [dsProgress, setDsProgress] = useState(0);
  const [dsResult, setDsResult] = useState(null);
  const [dsError, setDsError] = useState(null);

  const handleDsFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (f) { setDsFile(f); setDsResult(null); setDsError(null); }
  };

  const uploadDsData = async () => {
    if (!dsFile) return;
    setDsUploading(true);
    setDsProgress(0);
    setDsError(null);
    try {
      const storage = getStorage();
      const storagePath = `inmocean-raw-data/data-sources/${Date.now()}_${dsFile.name}`;
      const storageRef = ref(storage, storagePath);
      await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, dsFile);
        uploadTask.on('state_changed',
          (snapshot) => setDsProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 50)),
          (error) => reject(error),
          () => resolve()
        );
      });
      setDsProgress(60);
      const functions = getFunctions();
      const processBIUpload = httpsCallable(functions, 'processBIUpload', { timeout: 600000 });
      const result = await processBIUpload({
        bucketName: 'actualizer2.firebasestorage.app',
        filePath: storagePath,
        tableName: 'imports_data_source_1',
        datasetName: 'inmocean_raw',
        tenantId
      });
      setDsProgress(100);
      setDsResult({ success: true, filename: dsFile.name, rowCount: result.data?.rowsInserted || result.data?.rowCount || 0 });
    } catch (err) {
      setDsError(err.message);
    } finally {
      setDsUploading(false);
    }
  };

  // Reset workflow
  const resetWorkflow = () => {
    setCurrentStep(1);
    setShippingFile(null);
    setShippingResult(null);
    setShippingError(null);
    setDutyFile(null);
    setDutyResult(null);
    setDutyError(null);
    setFreightFile(null);
    setFreightResult(null);
    setFreightError(null);
    setBookingFile(null);
    setBookingResult(null);
    setBookingError(null);
    setMatsonDimsFile(null);
    setMatsonDimsResult(null);
    setMatsonDimsError(null);
    setTransformResult(null);
    setTransformError(null);
  };

  const StepIndicator = ({ step, title, isActive, isCompleted }) => (
    <div className={`flex flex-col items-center gap-1 ${isActive ? 'opacity-100' : 'opacity-60'}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
        isCompleted ? 'bg-green-500 text-white' :
        isActive ? 'bg-emerald-600 text-white' :
        'bg-gray-200 text-gray-600'
      }`}>
        {isCompleted ? <CheckCircle className="w-4 h-4" /> : step}
      </div>
      <span className={`text-xs font-medium text-center ${isActive ? 'text-emerald-700' : 'text-gray-500'}`}>{title}</span>
    </div>
  );

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  // Info popup component
  const InfoPopup = ({ tableName, onClose }) => {
    const status = tableStatuses[tableName];
    return (
      <div className="absolute z-50 top-0 right-0 mt-8 mr-1 w-64 bg-white rounded-lg shadow-xl border border-gray-200 p-3 text-xs">
        <div className="flex justify-between items-center mb-2">
          <span className="font-semibold text-gray-800">Table Info</span>
          <button onClick={onClose} className="p-0.5 hover:bg-gray-100 rounded">
            <X className="w-3 h-3 text-gray-500" />
          </button>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between">
            <span className="text-gray-500">Table:</span>
            <span className="font-mono text-gray-800">{tableName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Dataset:</span>
            <span className="font-mono text-gray-800">inmocean_raw</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Last Updated:</span>
            <span className="text-gray-800">{status ? formatTimestamp(status.last_updated) : 'Never'}</span>
          </div>
          {status?.row_count !== undefined && (
            <div className="flex justify-between">
              <span className="text-gray-500">Rows:</span>
              <span className="text-gray-800">{status.row_count?.toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Compact card for grid layout
  const CompactCard = ({ stepNum, title, subtitle, icon: Icon, isActive, isCompleted, children, borderColor = 'gray', tableName }) => (
    <div className={`bg-white rounded-lg border-2 ${
      isCompleted ? 'border-green-300 bg-green-50' :
      isActive ? `border-${borderColor}-400 ring-2 ring-${borderColor}-100` :
      'border-gray-200'
    } p-4 h-full relative`}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
          isCompleted ? 'bg-green-500 text-white' :
          isActive ? 'bg-emerald-600 text-white' :
          'bg-gray-200 text-gray-600'
        }`}>
          {isCompleted ? <CheckCircle className="w-3.5 h-3.5" /> : stepNum}
        </div>
        <Icon className={`h-5 w-5 ${isCompleted ? 'text-green-600' : isActive ? 'text-emerald-600' : 'text-gray-400'}`} />
        <div className="flex-1">
          <span className="text-sm font-semibold text-gray-800">{title}</span>
          {subtitle && <p className="text-[10px] text-gray-500">{subtitle}</p>}
        </div>
        {tableName && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setActiveInfoPopup(activeInfoPopup === tableName ? null : tableName);
            }}
            className="p-0.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
            title="View table info"
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {activeInfoPopup === tableName && (
        <InfoPopup tableName={tableName} onClose={() => setActiveInfoPopup(null)} />
      )}
      <div className="text-xs">
        {children}
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald-100 rounded-xl">
              <DollarSign className="h-7 w-7 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Actual Cost Workflow</h1>
              <p className="text-gray-600 text-sm">Upload broker data to calculate actual landed costs</p>
            </div>
          </div>
          <button
            onClick={resetWorkflow}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg text-sm"
          >
            <RefreshCw className="h-4 w-4" />
            Reset
          </button>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 overflow-x-auto">
        <div className="flex items-center justify-center gap-2 min-w-max">
          {/* Broker Group */}
          <div className="flex flex-col items-center">
            <span className="text-[10px] font-semibold text-emerald-600 mb-2 uppercase tracking-wide">Broker</span>
            <div className="flex items-center gap-1 bg-emerald-50 rounded-lg px-3 py-2 border border-emerald-200">
              <StepIndicator step={2} title="Duty" isActive={currentStep === 2} isCompleted={!!dutyResult} />
              <ChevronRight className="w-3 h-3 text-emerald-300 flex-shrink-0" />
              <StepIndicator step={3} title="Freight" isActive={currentStep === 3} isCompleted={!!freightResult} />
            </div>
          </div>

          <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />

          {/* Matson Group */}
          <div className="flex flex-col items-center">
            <span className="text-[10px] font-semibold text-blue-600 mb-2 uppercase tracking-wide">Matson</span>
            <div className="flex items-center gap-1 bg-blue-50 rounded-lg px-3 py-2 border border-blue-200">
              <StepIndicator step={1} title="Shipping" isActive={currentStep === 1} isCompleted={!!shippingResult} />
              <ChevronRight className="w-3 h-3 text-blue-300 flex-shrink-0" />
              <StepIndicator step="1b" title="Booking" isActive={currentStep === 1} isCompleted={!!bookingResult} />
              <ChevronRight className="w-3 h-3 text-blue-300 flex-shrink-0" />
              <StepIndicator step="1c" title="Dims" isActive={currentStep === 1} isCompleted={!!matsonDimsResult} />
            </div>
          </div>

          <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />

          {/* Transform Group */}
          <div className="flex flex-col items-center">
            <span className="text-[10px] font-semibold text-teal-600 mb-2 uppercase tracking-wide">Transform</span>
            <div className="flex items-center gap-1 bg-teal-50 rounded-lg px-3 py-2 border border-teal-200">
              <StepIndicator step={4} title="Run" isActive={currentStep === 4} isCompleted={!!transformResult} />
              <ChevronRight className="w-3 h-3 text-teal-300 flex-shrink-0" />
              <StepIndicator step={5} title="Done" isActive={currentStep === 5} isCompleted={false} />
            </div>
          </div>
        </div>
      </div>

      {/* ========== BROKER - FREIGHT COST AND DUTY CHARGES ========== */}
      <div className="flex items-center gap-3 mt-2">
        <div className="h-px flex-1 bg-emerald-200"></div>
        <span className="text-sm font-semibold text-emerald-600 uppercase tracking-wide flex items-center gap-2">
          <FileText className="h-4 w-4" /> Broker - Freight Cost and Duty Charges
        </span>
        <div className="h-px flex-1 bg-emerald-200"></div>
      </div>

      {/* Broker Row - 2 cards */}
      <div className="grid grid-cols-2 gap-4">
        {/* Step 2: Duty Data */}
        <CompactCard
          stepNum={2}
          title="Duty Charges"
          subtitle="Broker"
          icon={Receipt}
          isActive={currentStep === 2}
          isCompleted={!!dutyResult}
          borderColor="emerald"
          tableName="ac_duty_charges_data"
        >
          {!dutyResult ? (
            <div className="space-y-2">
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleDutyFileSelect} className="hidden" id="duty-file-input" />
              <label htmlFor="duty-file-input" className="flex items-center justify-center gap-1 px-3 py-2 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100 cursor-pointer border border-emerald-200">
                <Upload className="h-3.5 w-3.5" /> {dutyFile ? dutyFile.name.substring(0, 20) + '...' : 'Select File'}
              </label>
              {dutyFile && (
                <button onClick={uploadDutyData} disabled={dutyUploading} className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                  {dutyUploading ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> {dutyProgress}%</> : <><Play className="h-3.5 w-3.5" /> Upload</>}
                </button>
              )}
              {dutyError && <div className="text-red-500 truncate text-[11px]">{dutyError}</div>}
            </div>
          ) : (
            <div className="text-green-600 font-medium">{dutyResult.rowCount?.toLocaleString()} rows uploaded</div>
          )}
        </CompactCard>

        {/* Step 3: Freight Data */}
        <CompactCard
          stepNum={3}
          title="Freight Costs"
          subtitle="Broker"
          icon={Anchor}
          isActive={currentStep === 3}
          isCompleted={!!freightResult}
          borderColor="emerald"
          tableName="ac_freight_cost_data"
        >
          {!freightResult ? (
            <div className="space-y-2">
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFreightFileSelect} className="hidden" id="freight-file-input" />
              <label htmlFor="freight-file-input" className="flex items-center justify-center gap-1 px-3 py-2 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100 cursor-pointer border border-emerald-200">
                <Upload className="h-3.5 w-3.5" /> {freightFile ? freightFile.name.substring(0, 20) + '...' : 'Select File'}
              </label>
              {freightFile && (
                <button onClick={uploadFreightData} disabled={freightUploading} className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                  {freightUploading ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> {freightProgress}%</> : <><Play className="h-3.5 w-3.5" /> Upload</>}
                </button>
              )}
              {freightError && <div className="text-red-500 truncate text-[11px]">{freightError}</div>}
            </div>
          ) : (
            <div className="text-green-600 font-medium">{freightResult.rowCount?.toLocaleString()} rows uploaded</div>
          )}
        </CompactCard>
      </div>

      {/* ========== MATSON SECTION ========== */}
      <div className="flex items-center gap-3 mt-4">
        <div className="h-px flex-1 bg-blue-200"></div>
        <span className="text-sm font-semibold text-blue-600 uppercase tracking-wide flex items-center gap-2">
          <Ship className="h-4 w-4" /> Matson
        </span>
        <div className="h-px flex-1 bg-blue-200"></div>
      </div>

      {/* Matson Row */}
      <div className="grid grid-cols-3 gap-4">
        {/* Step 1: Shipping Data */}
        <CompactCard
          stepNum={1}
          title="Shipping Data"
          subtitle="PO Manager"
          icon={Ship}
          isActive={currentStep === 1}
          isCompleted={!!shippingResult}
          borderColor="blue"
          tableName="ac_shipping_data"
        >
          {!shippingResult ? (
            <div className="space-y-2">
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleShippingFileSelect} className="hidden" id="shipping-file-input" />
              <label htmlFor="shipping-file-input" className="flex items-center justify-center gap-1 px-3 py-2 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 cursor-pointer border border-blue-200">
                <Upload className="h-3.5 w-3.5" /> {shippingFile ? shippingFile.name.substring(0, 20) + '...' : 'Select File'}
              </label>
              {shippingFile && (
                <button onClick={uploadShippingData} disabled={shippingUploading} className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                  {shippingUploading ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> {shippingProgress}%</> : <><Play className="h-3.5 w-3.5" /> Upload</>}
                </button>
              )}
              {shippingError && <div className="text-red-500 truncate text-[11px]">{shippingError}</div>}
            </div>
          ) : (
            <div className="text-green-600 font-medium">{shippingResult.rowCount?.toLocaleString()} rows uploaded</div>
          )}
        </CompactCard>

        {/* Step 1b: Booking Data */}
        <CompactCard
          stepNum="1b"
          title="Booking Data"
          subtitle="Matson"
          icon={FileText}
          isActive={currentStep === 1}
          isCompleted={!!bookingResult}
          borderColor="blue"
          tableName="ac_booking_data"
        >
          {!bookingResult ? (
            <div className="space-y-2">
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleBookingFileSelect} className="hidden" id="booking-file-input" />
              <label htmlFor="booking-file-input" className="flex items-center justify-center gap-1 px-3 py-2 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 cursor-pointer border border-blue-200">
                <Upload className="h-3.5 w-3.5" /> {bookingFile ? bookingFile.name.substring(0, 20) + '...' : 'Select File'}
              </label>
              {bookingFile && (
                <button onClick={uploadBookingData} disabled={bookingUploading} className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                  {bookingUploading ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> {bookingProgress}%</> : <><Play className="h-3.5 w-3.5" /> Upload</>}
                </button>
              )}
              {bookingError && <div className="text-red-500 truncate text-[11px]">{bookingError}</div>}
            </div>
          ) : (
            <div className="text-green-600 font-medium">{bookingResult.rowCount?.toLocaleString()} rows uploaded</div>
          )}
        </CompactCard>

        {/* Step 1c: Matson Carton Dims */}
        <CompactCard
          stepNum="1c"
          title="Carton Dims"
          subtitle="Matson"
          icon={FileSpreadsheet}
          isActive={currentStep === 1}
          isCompleted={!!matsonDimsResult}
          borderColor="blue"
          tableName="matson_carton_dims"
        >
          {!matsonDimsResult ? (
            <div className="space-y-2">
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleMatsonDimsFileSelect} className="hidden" id="matson-dims-file-input" />
              <label htmlFor="matson-dims-file-input" className="flex items-center justify-center gap-1 px-3 py-2 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 cursor-pointer border border-blue-200">
                <Upload className="h-3.5 w-3.5" /> {matsonDimsFile ? matsonDimsFile.name.substring(0, 20) + '...' : 'Select File'}
              </label>
              {matsonDimsFile && (
                <button onClick={uploadMatsonCartonDims} disabled={matsonDimsUploading} className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                  {matsonDimsUploading ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> {matsonDimsProgress}%</> : <><Play className="h-3.5 w-3.5" /> Upload</>}
                </button>
              )}
              {matsonDimsError && <div className="text-red-500 truncate text-[11px]">{matsonDimsError}</div>}
            </div>
          ) : (
            <div className="text-green-600 font-medium">{matsonDimsResult.rowCount?.toLocaleString()} rows uploaded</div>
          )}
        </CompactCard>
      </div>

      {/* ========== TRANSFORM SECTION ========== */}
      <div className="flex items-center gap-3 mt-4">
        <div className="h-px flex-1 bg-teal-200"></div>
        <span className="text-sm font-semibold text-teal-600 uppercase tracking-wide flex items-center gap-2">
          <Zap className="h-4 w-4" /> Data Transformation
        </span>
        <div className="h-px flex-1 bg-teal-200"></div>
      </div>

      {/* Transform Row */}
      <div className="grid grid-cols-3 gap-4">
        {/* Step 4: Transform */}
        <CompactCard
          stepNum={4}
          title="Transform"
          subtitle="Create enriched tables"
          icon={Zap}
          isActive={currentStep === 4}
          isCompleted={!!transformResult}
          borderColor="teal"
          tableName="ac_shipping_data_enriched"
        >
          {!transformResult ? (
            <div className="space-y-2">
              <p className="text-gray-500 text-[11px] mb-2">Creates enriched tables for cost allocation view</p>
              <button onClick={runTransform} disabled={transforming || (!shippingResult && !dutyResult && !freightResult)} className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50">
                {transforming ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Running...</> : <><Zap className="h-3.5 w-3.5" /> Run Transform</>}
              </button>
              {transformError && <div className="text-red-500 truncate text-[11px]">{transformError}</div>}
            </div>
          ) : (
            <div className="text-green-600 font-medium">
              {transformResult.rowCount?.toLocaleString() || 'Complete'} rows processed
            </div>
          )}
        </CompactCard>

        {/* Step 5: Done */}
        <CompactCard
          stepNum={5}
          title="Complete"
          subtitle="Ready for view"
          icon={CheckCircle}
          isActive={currentStep === 5}
          isCompleted={false}
          borderColor="green"
        >
          {currentStep === 5 ? (
            <div className="space-y-2">
              <div className="text-green-600 font-medium">Workflow complete!</div>
              <p className="text-gray-500 text-[11px]">Enriched tables are ready. The cost allocation view will now use the updated data.</p>
              <button onClick={resetWorkflow} className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                <RefreshCw className="h-3.5 w-3.5" /> Start New
              </button>
            </div>
          ) : (
            <div className="text-gray-400 text-[11px]">Waiting for transform to complete...</div>
          )}
        </CompactCard>

        {/* Placeholder */}
        <div className="border-2 border-dashed border-teal-200 rounded-lg p-4 flex items-center justify-center text-teal-400 text-xs">
          Output tables: ac_shipping_data_enriched, ac_duty_data_enriched, ac_freight_data_enriched, ac_booking_data_enriched
        </div>
      </div>

      {/* ========== DATA SOURCES SECTION ========== */}
      <div className="flex items-center gap-3 mt-4">
        <div className="h-px flex-1 bg-purple-200"></div>
        <span className="text-sm font-semibold text-purple-600 uppercase tracking-wide flex items-center gap-2">
          <Database className="h-4 w-4" /> Data Sources
        </span>
        <div className="h-px flex-1 bg-purple-200"></div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <CompactCard
          stepNum="DS"
          title="Data Source 1"
          subtitle="External data"
          icon={Database}
          isActive={!dsResult}
          isCompleted={!!dsResult}
          borderColor="purple"
          tableName="imports_data_source_1"
        >
          {!dsResult ? (
            <div className="space-y-2">
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleDsFileSelect} className="hidden" id="ds-file-input" />
              <label htmlFor="ds-file-input" className="flex items-center justify-center gap-1 px-3 py-2 bg-purple-50 text-purple-700 rounded hover:bg-purple-100 cursor-pointer border border-purple-200">
                <Upload className="h-3.5 w-3.5" /> {dsFile ? dsFile.name.substring(0, 25) + '...' : 'Select File'}
              </label>
              {dsFile && (
                <button onClick={uploadDsData} disabled={dsUploading} className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50">
                  {dsUploading ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> {dsProgress}%</> : <><Play className="h-3.5 w-3.5" /> Upload</>}
                </button>
              )}
              {dsError && <div className="text-red-500 truncate text-[11px]">{dsError}</div>}
            </div>
          ) : (
            <div className="text-green-600 font-medium">{dsResult.rowCount?.toLocaleString()} rows uploaded</div>
          )}
        </CompactCard>
      </div>
    </div>
  );
}
